import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { htmlSafe } from '@ember/template';
import { LinkTo } from '@ember/routing';
import { Section, VStack } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import Chip from 'tio-ui/components/chip';
import { getCurrentDemoClientData } from 'tio-employee/utils/career-pathways-demo-data';
import PageHero from 'tio-ui/components/page-hero';
import Modal from 'tio-ui/components/modal';
import {
  PhArrowLeft,
  PhArrowsLeftRight,
  PhBriefcase,
  PhCurrencyDollar,
  PhTrendUp,
  PhGraduationCap,
  PhCheckCircle,
  PhX,
  PhTarget,
  PhClock,
  PhUsers,
  PhSparkle,
  PhPath,
} from 'ember-phosphor-icons';

// Roles available for comparison
const HEALTHCARE_COMPARISON_ROLES = [
  {
    id: 'nurse-manager',
    title: 'Nurse Manager',
    family: 'Nursing',
    level: 'Management',
    salaryMin: 85000,
    salaryMax: 120000,
    salaryMedian: 102000,
    growthRate: 18,
    openings: 8,
    matchScore: 68,
    timeToReady: '18 months',
    requiredSkills: [
      { name: 'Leadership', current: 65, required: 80, gap: true },
      { name: 'Budget Management', current: 0, required: 75, gap: true },
      { name: 'Staff Scheduling', current: 20, required: 70, gap: true },
      { name: 'Quality Improvement', current: 35, required: 75, gap: true },
      { name: 'Patient Care', current: 95, required: 70, gap: false },
      { name: 'Epic EMR', current: 85, required: 70, gap: false },
    ],
    requiredCredentials: ['BSN Degree', 'RN License', '5+ years clinical experience'],
    preferredCredentials: ['MSN Degree', 'Nurse Leadership Certificate', 'Lean Six Sigma'],
    dayInLife: [
      'Manage staff schedules and assignments',
      'Conduct morning huddles with team',
      'Monitor patient care quality metrics',
      'Handle staffing issues and call-offs',
      'Attend hospital leadership meetings',
      'Conduct performance reviews',
    ],
    pros: [
      'Higher salary and leadership responsibility',
      'Direct impact on patient care quality',
      'Career growth to Director/CNO roles',
      'Develops business & management skills',
    ],
    cons: [
      '24/7 accountability for unit',
      'More administrative work, less patient care',
      'Managing difficult personnel situations',
      'Budget pressures and cost management',
    ],
  },
  {
    id: 'clinical-educator',
    title: 'Clinical Nurse Educator',
    family: 'Education & Training',
    level: 'Senior',
    salaryMin: 75000,
    salaryMax: 100000,
    salaryMedian: 87000,
    growthRate: 14,
    openings: 4,
    matchScore: 92,
    timeToReady: '6 months',
    requiredSkills: [
      { name: 'Teaching/Training', current: 85, required: 85, gap: false },
      { name: 'Curriculum Development', current: 60, required: 75, gap: true },
      { name: 'Clinical Expertise', current: 95, required: 90, gap: false },
      { name: 'Communication', current: 90, required: 85, gap: false },
      { name: 'Patient Care', current: 95, required: 80, gap: false },
      { name: 'Epic EMR', current: 85, required: 80, gap: false },
    ],
    requiredCredentials: ['BSN Degree', 'RN License', '3+ years clinical experience'],
    preferredCredentials: ['MSN in Nursing Education', 'ANCC Certification', 'Simulation Training'],
    dayInLife: [
      'Develop and update training curricula',
      'Conduct new nurse orientation',
      'Run skills labs and simulations',
      'Mentor nurses on unit',
      'Evaluate competencies',
      'Stay current on evidence-based practice',
    ],
    pros: [
      'Strong match with your current skills',
      'Share knowledge and mentor others',
      'Regular weekday schedule (usually)',
      'Continuous learning and teaching',
    ],
    cons: [
      'Lower salary than management track',
      'May feel removed from direct patient care',
      'Requires staying current on all areas',
      'Can be repetitive (same orientations)',
    ],
  },
  {
    id: 'charge-nurse',
    title: 'Charge Nurse',
    family: 'Nursing',
    level: 'Senior',
    salaryMin: 72000,
    salaryMax: 95000,
    salaryMedian: 83000,
    growthRate: 16,
    openings: 15,
    matchScore: 95,
    timeToReady: '3 months',
    requiredSkills: [
      { name: 'Leadership', current: 65, required: 70, gap: false },
      { name: 'Patient Care', current: 95, required: 85, gap: false },
      { name: 'Crisis Management', current: 70, required: 75, gap: true },
      { name: 'Team Coordination', current: 80, required: 80, gap: false },
      { name: 'Clinical Protocols', current: 90, required: 85, gap: false },
      { name: 'Epic EMR', current: 85, required: 80, gap: false },
    ],
    requiredCredentials: ['BSN Degree', 'RN License', '3+ years clinical experience'],
    preferredCredentials: ['Charge Nurse Training', 'ACLS Certification'],
    dayInLife: [
      'Oversee shift operations',
      'Make patient assignments',
      'Handle immediate staffing issues',
      'Respond to clinical emergencies',
      'Coordinate with physicians',
      'Support and mentor staff',
    ],
    pros: [
      'Excellent stepping stone to management',
      'Still involved in patient care',
      'Leadership experience without full admin burden',
      'Highest match score for you!',
    ],
    cons: [
      'Caught between staff and management',
      "Limited authority - responsibility without 'power'",
      'First call for all shift problems',
      'Salary increase smaller than manager',
    ],
  },
];

const UPS_COMPARISON_ROLES = [
  {
    id: 'hub-supervisor',
    title: 'Hub Supervisor',
    family: 'Operations Leadership',
    level: 'Management',
    salaryMin: 68000,
    salaryMax: 92000,
    salaryMedian: 80000,
    growthRate: 12,
    openings: 18,
    matchScore: 72,
    timeToReady: '12 months',
    requiredSkills: [
      { name: 'Leadership', current: 62, required: 78, gap: true },
      { name: 'Safety Compliance', current: 82, required: 75, gap: false },
      { name: 'Scheduling', current: 20, required: 70, gap: true },
      { name: 'Process Improvement', current: 45, required: 70, gap: true },
      { name: 'Operations Metrics', current: 70, required: 70, gap: false },
      { name: 'Team Collaboration', current: 85, required: 75, gap: false },
    ],
    requiredCredentials: ['Safety Leadership Training', '2+ years operations experience'],
    preferredCredentials: ['OSHA Compliance Certificate', 'Lean Fundamentals'],
    dayInLife: [
      'Lead shift kickoff and safety briefings',
      'Monitor volume and throughput targets',
      'Adjust staffing for on-time performance',
      'Coach supervisors and team leads',
      'Review incident reports and compliance logs',
      'Partner with dispatch and maintenance teams',
    ],
    pros: [
      'Clear path to operations leadership',
      'Direct impact on safety and service metrics',
      'High demand across hubs',
      'Strong development opportunities',
    ],
    cons: [
      'Peak season pressure',
      'Shift coverage expectations',
      'Frequent staffing adjustments',
      'Balancing safety and productivity targets',
    ],
  },
  {
    id: 'operations-manager',
    title: 'Operations Manager',
    family: 'Logistics Management',
    level: 'Senior',
    salaryMin: 85000,
    salaryMax: 115000,
    salaryMedian: 98000,
    growthRate: 10,
    openings: 8,
    matchScore: 60,
    timeToReady: '18 months',
    requiredSkills: [
      { name: 'Leadership', current: 62, required: 85, gap: true },
      { name: 'Budget Management', current: 30, required: 75, gap: true },
      { name: 'Continuous Improvement', current: 45, required: 80, gap: true },
      { name: 'Operations Analytics', current: 55, required: 75, gap: true },
      { name: 'Safety Compliance', current: 82, required: 80, gap: false },
      { name: 'Stakeholder Management', current: 60, required: 70, gap: true },
    ],
    requiredCredentials: ['3+ years operations leadership', 'Safety Compliance Training'],
    preferredCredentials: ['Lean Operations Certificate', 'Project Management'],
    dayInLife: [
      'Review hub performance metrics',
      'Plan staffing and labor budgets',
      'Lead continuous improvement initiatives',
      'Coordinate with regional leadership',
      'Resolve escalation issues',
      'Coach supervisors on performance',
    ],
    pros: [
      'Higher compensation and influence',
      'Strategic role in hub performance',
      'Leads multiple teams',
      'Strong growth path',
    ],
    cons: [
      'More complex stakeholder demands',
      'Greater accountability for metrics',
      'Less hands-on operations work',
      'Longer readiness timeline',
    ],
  },
  {
    id: 'feeder-driver',
    title: 'Feeder Driver (CDL-A)',
    family: 'Transportation',
    level: 'Senior',
    salaryMin: 78000,
    salaryMax: 105000,
    salaryMedian: 90000,
    growthRate: 14,
    openings: 25,
    matchScore: 78,
    timeToReady: '9 months',
    requiredSkills: [
      { name: 'CDL-A License', current: 0, required: 100, gap: true },
      { name: 'Safety Compliance', current: 82, required: 85, gap: true },
      { name: 'Route Planning', current: 50, required: 70, gap: true },
      { name: 'Equipment Inspection', current: 60, required: 80, gap: true },
      { name: 'Time Management', current: 85, required: 70, gap: false },
      { name: 'Operations Knowledge', current: 70, required: 70, gap: false },
    ],
    requiredCredentials: ['CDL-A License', 'Safety Training'],
    preferredCredentials: ['Hazmat Endorsement'],
    dayInLife: [
      'Complete pre-trip safety inspections',
      'Transport trailers between hubs',
      'Log mileage and compliance reports',
      'Coordinate with dispatch on routes',
      'Maintain safe driving standards',
      'Report equipment issues',
    ],
    pros: [
      'Strong compensation potential',
      'Clear certification path',
      'Independent work style',
      'High demand role',
    ],
    cons: [
      'Certification required',
      'Longer shifts on the road',
      'Less team leadership',
      'Strict compliance requirements',
    ],
  },
];

const STARBUCKS_COMPARISON_ROLES = [
  {
    id: 'store-manager',
    title: 'Store Manager',
    family: 'Store Leadership',
    level: 'Management',
    salaryMin: 62000,
    salaryMax: 88000,
    salaryMedian: 75000,
    growthRate: 10,
    openings: 22,
    matchScore: 74,
    timeToReady: '15 months',
    requiredSkills: [
      { name: 'Leadership', current: 68, required: 80, gap: true },
      { name: 'Labor Planning', current: 25, required: 70, gap: true },
      { name: 'Customer Experience', current: 90, required: 85, gap: false },
      { name: 'Inventory Management', current: 40, required: 70, gap: true },
      { name: 'Coaching', current: 60, required: 75, gap: true },
      { name: 'Quality Standards', current: 80, required: 75, gap: false },
    ],
    requiredCredentials: ['Shift Supervisor experience', 'Food Safety Certification'],
    preferredCredentials: ['Store Manager Development'],
    dayInLife: [
      'Lead opening huddles and day-part planning',
      'Coach partners on service standards',
      'Adjust labor schedules for demand',
      'Review inventory and ordering',
      'Handle customer escalations',
      'Partner with district leadership',
    ],
    pros: [
      'Direct impact on store performance',
      'Strong leadership growth opportunities',
      'Clear career ladder to district roles',
      'Customer-facing leadership',
    ],
    cons: [
      'High accountability for store metrics',
      'Frequent staffing adjustments',
      'Peak season pressure',
      'Requires strong coaching skills',
    ],
  },
  {
    id: 'shift-supervisor',
    title: 'Shift Supervisor',
    family: 'Retail Operations',
    level: 'Senior',
    salaryMin: 38000,
    salaryMax: 48000,
    salaryMedian: 43000,
    growthRate: 12,
    openings: 40,
    matchScore: 92,
    timeToReady: '6 months',
    requiredSkills: [
      { name: 'Customer Experience', current: 90, required: 85, gap: false },
      { name: 'Team Coordination', current: 78, required: 80, gap: true },
      { name: 'Cash Handling', current: 80, required: 80, gap: false },
      { name: 'Scheduling', current: 55, required: 70, gap: true },
      { name: 'Quality Standards', current: 80, required: 75, gap: false },
      { name: 'Coaching', current: 60, required: 70, gap: true },
    ],
    requiredCredentials: ['Shift Lead Certification'],
    preferredCredentials: ['Store Operations Workshop'],
    dayInLife: [
      'Lead shift floor coverage',
      'Coach baristas on service and quality',
      'Balance staffing for peak periods',
      'Handle customer feedback',
      'Maintain store readiness',
      'Coordinate with store manager',
    ],
    pros: [
      'Strong match with current skills',
      'Fast pathway to management',
      'Hands-on leadership experience',
      'High demand across stores',
    ],
    cons: [
      'High pace during peak hours',
      'Weekend/holiday shifts',
      'Balancing people and operational tasks',
      'Lower pay than manager roles',
    ],
  },
  {
    id: 'district-ops',
    title: 'District Operations Lead',
    family: 'Operations',
    level: 'Senior',
    salaryMin: 90000,
    salaryMax: 120000,
    salaryMedian: 104000,
    growthRate: 8,
    openings: 6,
    matchScore: 58,
    timeToReady: '20 months',
    requiredSkills: [
      { name: 'Multi-unit Leadership', current: 30, required: 75, gap: true },
      { name: 'Financial Management', current: 35, required: 80, gap: true },
      { name: 'Operational Strategy', current: 45, required: 75, gap: true },
      { name: 'Coaching', current: 60, required: 80, gap: true },
      { name: 'Customer Experience', current: 90, required: 85, gap: false },
      { name: 'Change Management', current: 40, required: 70, gap: true },
    ],
    requiredCredentials: ['Store Manager experience', 'Retail Operations Training'],
    preferredCredentials: ['Leadership Development Program'],
    dayInLife: [
      'Support store managers across the district',
      'Review operational KPIs',
      'Lead district-wide initiatives',
      'Partner on staffing and retention plans',
      'Coach leaders on performance',
      'Drive customer experience improvements',
    ],
    pros: [
      'Strategic leadership role',
      'Influence across multiple stores',
      'Strong compensation',
      'Pathway to regional leadership',
    ],
    cons: [
      'Longer readiness timeline',
      'Frequent travel between stores',
      'High accountability for district metrics',
      'Less day-to-day team interaction',
    ],
  },
];

export default class CareerPathwaysExploreCompare extends Component {
  @tracked leftRole = this.comparisonRoles[0];
  @tracked rightRole = this.comparisonRoles[1] ?? this.comparisonRoles[0];
  @tracked showRoleSelector: 'left' | 'right' | null = null;

  get comparisonRoles() {
    const clientId = getCurrentDemoClientData().client.id;

    if (clientId === 'ups') {
      return UPS_COMPARISON_ROLES;
    }

    if (clientId === 'starbucks') {
      return STARBUCKS_COMPARISON_ROLES;
    }

    return HEALTHCARE_COMPARISON_ROLES;
  }

  get availableRoles() {
    return this.comparisonRoles;
  }

  get activeSelectorSide() {
    return this.showRoleSelector ?? 'left';
  }

  selectRole = (side: 'left' | 'right', roleId: string) => {
    const role = this.comparisonRoles.find((r) => r.id === roleId);
    if (role) {
      if (side === 'left') {
        this.leftRole = role;
      } else {
        this.rightRole = role;
      }
    }
    this.showRoleSelector = null;
  };

  openSelector = (side: 'left' | 'right') => {
    this.showRoleSelector = side;
  };

  closeSelector = () => {
    this.showRoleSelector = null;
  };

  progressStyle = (value: number) => {
    return htmlSafe(`width: ${value}%`);
  };

  swapRoles = () => {
    const temp = this.leftRole;
    this.leftRole = this.rightRole;
    this.rightRole = temp;
  };

  getMatchColor = (score: number | undefined) => {
    if ((score ?? 0) >= 90) return 'tio-fg-success tio-bg-success-soft';
    if ((score ?? 0) >= 70) return 'tio-fg-brand tio-bg-brand-soft';
    return 'tio-fg-warning tio-bg-warning-soft';
  };

  getSkillStatus = (current: number, required: number) => {
    if (current >= required) return { color: 'tio-bg-success', status: 'Met' };
    if (current >= required * 0.7) return { color: 'tio-bg-warning', status: 'Close' };
    return { color: 'tio-bg-danger', status: 'Gap' };
  };

  formatSalary = (amount: number | undefined) => {
    return `$${((amount ?? 0) / 1000).toFixed(0)}K`;
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="data-audit w-full min-w-0">
      {{! Back navigation }}
      <div class="mb-4">
        <LinkTo
          @route="authenticated.reimagined.explore"
          class="tio-inline-row gap-2 tio-fg-brand hover:tio-fg-brand font-medium"
        >
          <PhArrowLeft @size={{18}} />
          Back to Explore Careers
        </LinkTo>
      </div>

      {{! Header }}
      <PageHero @gradient="brand">
        <:icon>
          <PhArrowsLeftRight @size={{32}} />
          <Heading @level={{1}} class="tio-fg-inverse">Compare Career Paths</Heading>
        </:icon>
        <:default>
          <Text @size="lg" class="tio-career-compare-hero-copy">
            See how different roles compare based on your skills, interests, and career goals.
          </Text>
        </:default>
      </PageHero>

      {{! Role Selector Modal }}
      <Modal
        @isOpen={{if this.showRoleSelector true false}}
        @onClose={{this.closeSelector}}
        @size="md"
      >
        <:header>
          <Heading @level={{3}}>Select a Role</Heading>
        </:header>
        <:body>
          <div class="space-y-3">
            {{#each this.availableRoles as |role|}}
              <button
                type="button"
                class="w-full text-left p-4 rounded-lg border-2 tio-border-default hover:tio-border-brand transition-colors"
                {{on "click" (fn this.selectRole this.activeSelectorSide role.id)}}
              >
                <div class="tio-row-between mb-2">
                  <span class="font-semibold tio-fg-primary">{{role.title}}</span>
                  <span
                    class="px-2 py-1 rounded-full tio-text-sm-medium
                      {{this.getMatchColor role.matchScore}}"
                  >
                    {{role.matchScore}}% Match
                  </span>
                </div>
                <div class="tio-row-loose text-sm tio-fg-muted">
                  <span>{{role.family}}</span>
                  <span>&bull;</span>
                  <span>{{this.formatSalary role.salaryMedian}} median</span>
                </div>
              </button>
            {{/each}}
          </div>
        </:body>
      </Modal>

      {{! Comparison Grid }}
      <div class="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-4 lg:gap-8">
        {{! Left Role }}
        <div class="space-y-4">
          {{! Role Header }}
          <button
            type="button"
            class="w-full tio-bg-surface rounded-xl shadow-sm p-6 text-left hover:shadow-md transition-shadow"
            {{on "click" (fn this.openSelector "left")}}
          >
            <div class="tio-row-between mb-3">
              <div class="tio-row">
                <div class="w-12 h-12 rounded-full tio-bg-brand-soft tio-row-center">
                  <PhBriefcase @size={{24}} class="tio-fg-brand" />
                </div>
                <div>
                  <Heading @level={{3}}>{{this.leftRole.title}}</Heading>
                  <Text @variant="muted" @size="sm">{{this.leftRole.family}}
                    •
                    {{this.leftRole.level}}</Text>
                </div>
              </div>
              <Text @variant="muted" @size="sm">Click to change</Text>
            </div>
            <div
              class="tio-inline-row gap-2 px-3 py-1.5 rounded-full
                {{this.getMatchColor this.leftRole.matchScore}}"
            >
              <PhTarget @size={{16}} />
              <span class="font-semibold">{{this.leftRole.matchScore}}% Match</span>
            </div>
          </button>

          {{! Key Metrics }}
          <Section class="tio-bg-surface rounded-lg shadow-sm">
            <:header>
              <span>Key Metrics</span>
            </:header>
            <:body>
              <div class="grid grid-cols-2 gap-4">
                <div class="text-center p-3 tio-surface-sunken">
                  <PhCurrencyDollar @size={{20}} class="mx-auto mb-1 tio-fg-success" />
                  <div class="font-bold tio-fg-primary">{{this.formatSalary
                      this.leftRole.salaryMin
                    }}
                    -
                    {{this.formatSalary this.leftRole.salaryMax}}</div>
                  <Text @variant="muted" @size="xs">Salary Range</Text>
                </div>
                <div class="text-center p-3 tio-surface-sunken">
                  <PhTrendUp @size={{20}} class="mx-auto mb-1 tio-fg-info" />
                  <div class="font-bold tio-fg-primary">+{{this.leftRole.growthRate}}%</div>
                  <Text @variant="muted" @size="xs">Job Growth</Text>
                </div>
                <div class="text-center p-3 tio-surface-sunken">
                  <PhUsers @size={{20}} class="mx-auto mb-1 tio-fg-brand" />
                  <div class="font-bold tio-fg-primary">{{this.leftRole.openings}}</div>
                  <Text @variant="muted" @size="xs">Open Positions</Text>
                </div>
                <div class="text-center p-3 tio-surface-sunken">
                  <PhClock @size={{20}} class="mx-auto mb-1 tio-fg-warning" />
                  <div class="font-bold tio-fg-primary">{{this.leftRole.timeToReady}}</div>
                  <Text @variant="muted" @size="xs">Time to Ready</Text>
                </div>
              </div>
            </:body>
          </Section>

          {{! Skills Comparison }}
          <Section class="tio-bg-surface rounded-lg shadow-sm">
            <:header>
              <span>Skills Required</span>
            </:header>
            <:body>
              <div class="space-y-3">
                {{#each this.leftRole.requiredSkills as |skill|}}
                  <div>
                    <div class="tio-row-between mb-1">
                      <span class="tio-text-sm-medium tio-fg-secondary">{{skill.name}}</span>
                      {{#if skill.gap}}
                        <Chip @intent="danger" @size="sm">Gap</Chip>
                      {{else}}
                        <Chip @intent="success" @size="sm">Ready</Chip>
                      {{/if}}
                    </div>
                    <div class="tio-row-tight">
                      <div class="flex-1 h-2 tio-bg-track rounded-full overflow-hidden">
                        <div
                          class="h-full rounded-full transition-all
                            {{if skill.gap 'tio-bg-danger' 'tio-bg-success'}}"
                          style={{this.progressStyle skill.current}}
                        ></div>
                      </div>
                      <span class="text-xs tio-fg-muted w-16 text-right">
                        {{skill.current}}/{{skill.required}}
                      </span>
                    </div>
                  </div>
                {{/each}}
              </div>
            </:body>
          </Section>

          {{! Pros & Cons }}
          <Section class="tio-bg-surface rounded-lg shadow-sm">
            <:header>
              <span>Pros & Cons</span>
            </:header>
            <:body>
              <div class="space-y-4">
                <div>
                  <Text @size="sm" @weight="semibold" class="tio-fg-success-body mb-2">Pros</Text>
                  <ul class="space-y-2">
                    {{#each this.leftRole.pros as |pro|}}
                      <li class="tio-row-top-tight text-sm tio-fg-secondary">
                        <PhCheckCircle @size={{16}} class="tio-fg-success shrink-0 mt-0.5" />
                        {{pro}}
                      </li>
                    {{/each}}
                  </ul>
                </div>
                <div>
                  <Text @size="sm" @weight="semibold" class="tio-fg-danger-body mb-2">Cons</Text>
                  <ul class="space-y-2">
                    {{#each this.leftRole.cons as |con|}}
                      <li class="tio-row-top-tight text-sm tio-fg-secondary">
                        <PhX @size={{16}} class="tio-fg-danger shrink-0 mt-0.5" />
                        {{con}}
                      </li>
                    {{/each}}
                  </ul>
                </div>
              </div>
            </:body>
          </Section>

          {{! Day in the Life }}
          <Section class="tio-bg-surface rounded-lg shadow-sm">
            <:header>
              <span>A Day in the Life</span>
            </:header>
            <:body>
              <ul class="space-y-2">
                {{#each this.leftRole.dayInLife as |activity|}}
                  <li class="tio-row-top-tight text-sm tio-fg-secondary">
                    <span class="tio-fg-brand-accent">•</span>
                    {{activity}}
                  </li>
                {{/each}}
              </ul>
            </:body>
          </Section>
        </div>

        {{! Center Swap Button }}
        <div class="hidden lg:flex flex-col items-center justify-start pt-32">
          <button
            type="button"
            class="p-3 tio-bg-surface rounded-full shadow-lg hover:shadow-xl hover:tio-bg-brand-wash transition-all"
            title="Swap roles"
            {{on "click" this.swapRoles}}
          >
            <PhArrowsLeftRight @size={{24}} class="tio-fg-brand" />
          </button>
        </div>

        {{! Right Role }}
        <div class="space-y-4">
          {{! Mobile Swap Button }}
          <div class="lg:hidden tio-flex-center">
            <button
              type="button"
              class="p-3 tio-bg-surface rounded-full shadow hover:shadow-md transition-all"
              title="Swap roles"
              {{on "click" this.swapRoles}}
            >
              <PhArrowsLeftRight @size={{20}} class="tio-fg-brand" />
            </button>
          </div>

          {{! Role Header }}
          <button
            type="button"
            class="w-full tio-bg-surface rounded-xl shadow-sm p-6 text-left hover:shadow-md transition-shadow"
            {{on "click" (fn this.openSelector "right")}}
          >
            <div class="tio-row-between mb-3">
              <div class="tio-row">
                <div class="w-12 h-12 rounded-full tio-bg-info-soft tio-row-center">
                  <PhGraduationCap @size={{24}} class="tio-fg-info" />
                </div>
                <div>
                  <Heading @level={{3}}>{{this.rightRole.title}}</Heading>
                  <Text @variant="muted" @size="sm">{{this.rightRole.family}}
                    •
                    {{this.rightRole.level}}</Text>
                </div>
              </div>
              <Text @variant="muted" @size="sm">Click to change</Text>
            </div>
            <div
              class="tio-inline-row gap-2 px-3 py-1.5 rounded-full
                {{this.getMatchColor this.rightRole.matchScore}}"
            >
              <PhTarget @size={{16}} />
              <span class="font-semibold">{{this.rightRole.matchScore}}% Match</span>
            </div>
          </button>

          {{! Key Metrics }}
          <Section class="tio-bg-surface rounded-lg shadow-sm">
            <:header>
              <span>Key Metrics</span>
            </:header>
            <:body>
              <div class="grid grid-cols-2 gap-4">
                <div class="text-center p-3 tio-surface-sunken">
                  <PhCurrencyDollar @size={{20}} class="mx-auto mb-1 tio-fg-success" />
                  <div class="font-bold tio-fg-primary">{{this.formatSalary
                      this.rightRole.salaryMin
                    }}
                    -
                    {{this.formatSalary this.rightRole.salaryMax}}</div>
                  <Text @variant="muted" @size="xs">Salary Range</Text>
                </div>
                <div class="text-center p-3 tio-surface-sunken">
                  <PhTrendUp @size={{20}} class="mx-auto mb-1 tio-fg-info" />
                  <div class="font-bold tio-fg-primary">+{{this.rightRole.growthRate}}%</div>
                  <Text @variant="muted" @size="xs">Job Growth</Text>
                </div>
                <div class="text-center p-3 tio-surface-sunken">
                  <PhUsers @size={{20}} class="mx-auto mb-1 tio-fg-brand" />
                  <div class="font-bold tio-fg-primary">{{this.rightRole.openings}}</div>
                  <Text @variant="muted" @size="xs">Open Positions</Text>
                </div>
                <div class="text-center p-3 tio-surface-sunken">
                  <PhClock @size={{20}} class="mx-auto mb-1 tio-fg-warning" />
                  <div class="font-bold tio-fg-primary">{{this.rightRole.timeToReady}}</div>
                  <Text @variant="muted" @size="xs">Time to Ready</Text>
                </div>
              </div>
            </:body>
          </Section>

          {{! Skills Comparison }}
          <Section class="tio-bg-surface rounded-lg shadow-sm">
            <:header>
              <span>Skills Required</span>
            </:header>
            <:body>
              <div class="space-y-3">
                {{#each this.rightRole.requiredSkills as |skill|}}
                  <div>
                    <div class="tio-row-between mb-1">
                      <span class="tio-text-sm-medium tio-fg-secondary">{{skill.name}}</span>
                      {{#if skill.gap}}
                        <Chip @intent="danger" @size="sm">Gap</Chip>
                      {{else}}
                        <Chip @intent="success" @size="sm">Ready</Chip>
                      {{/if}}
                    </div>
                    <div class="tio-row-tight">
                      <div class="flex-1 h-2 tio-bg-track rounded-full overflow-hidden">
                        <div
                          class="h-full rounded-full transition-all
                            {{if skill.gap 'tio-bg-danger' 'tio-bg-success'}}"
                          style={{this.progressStyle skill.current}}
                        ></div>
                      </div>
                      <span class="text-xs tio-fg-muted w-16 text-right">
                        {{skill.current}}/{{skill.required}}
                      </span>
                    </div>
                  </div>
                {{/each}}
              </div>
            </:body>
          </Section>

          {{! Pros & Cons }}
          <Section class="tio-bg-surface rounded-lg shadow-sm">
            <:header>
              <span>Pros & Cons</span>
            </:header>
            <:body>
              <div class="space-y-4">
                <div>
                  <Text @size="sm" @weight="semibold" class="tio-fg-success-body mb-2">Pros</Text>
                  <ul class="space-y-2">
                    {{#each this.rightRole.pros as |pro|}}
                      <li class="tio-row-top-tight text-sm tio-fg-secondary">
                        <PhCheckCircle @size={{16}} class="tio-fg-success shrink-0 mt-0.5" />
                        {{pro}}
                      </li>
                    {{/each}}
                  </ul>
                </div>
                <div>
                  <Text @size="sm" @weight="semibold" class="tio-fg-danger-body mb-2">Cons</Text>
                  <ul class="space-y-2">
                    {{#each this.rightRole.cons as |con|}}
                      <li class="tio-row-top-tight text-sm tio-fg-secondary">
                        <PhX @size={{16}} class="tio-fg-danger shrink-0 mt-0.5" />
                        {{con}}
                      </li>
                    {{/each}}
                  </ul>
                </div>
              </div>
            </:body>
          </Section>

          {{! Day in the Life }}
          <Section class="tio-bg-surface rounded-lg shadow-sm">
            <:header>
              <span>A Day in the Life</span>
            </:header>
            <:body>
              <ul class="space-y-2">
                {{#each this.rightRole.dayInLife as |activity|}}
                  <li class="tio-row-top-tight text-sm tio-fg-secondary">
                    <span class="tio-fg-info">•</span>
                    {{activity}}
                  </li>
                {{/each}}
              </ul>
            </:body>
          </Section>
        </div>
      </div>

      {{! Bottom CTA }}
      <div class="mt-8 tio-gradient-brand rounded-xl p-6 tio-fg-inverse">
        <div class="tio-row-start-between flex-col md:flex-row md:items-center gap-4">
          <div>
            <Heading @level={{3}} class="tio-fg-inverse mb-1">Ready to choose your path?</Heading>
            <Text class="tio-career-compare-hero-copy">
              Talk to the AI Advisor to get personalized guidance on which role is right for you.
            </Text>
          </div>
          <div class="tio-flex">
            <LinkTo
              @route="authenticated.reimagined.advisor"
              class="tio-inline-row gap-2 px-4 py-2 tio-bg-surface tio-fg-brand rounded-lg font-medium hover:tio-bg-brand-wash transition-colors"
            >
              <PhSparkle @size={{18}} />
              Ask AI Advisor
            </LinkTo>
            <LinkTo
              @route="authenticated.reimagined.plan.new"
              class="tio-inline-row gap-2 px-4 py-2 tio-bg-brand-accent tio-fg-inverse rounded-lg font-medium tio-career-compare-plan-link transition-colors"
            >
              <PhPath @size={{18}} />
              Create Plan
            </LinkTo>
          </div>
        </div>
      </div>
    </VStack>
  </template>
}
